/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useRef } from 'preact/compat';
import { classes, VComponent, getFormValues } from '@adobe-commerce/elsie/lib';
import { Accordion, AccordionSection } from '@adobe-commerce/elsie/components';
import { Add, Minus, Coupon } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/cart/components/Coupons/Coupons.css';

export interface CouponsProps extends HTMLAttributes<HTMLDivElement> {
  accordionSectionTitle?: string;
  accordionSectionIcon?: string;
  couponCodeField?: VNode<HTMLAttributes<HTMLInputElement>>;
  applyCouponsButton?: VNode<HTMLAttributes<HTMLButtonElement>>;
  appliedCoupons?: VNode<HTMLAttributes<HTMLDivElement>>;
  error?: VNode<HTMLAttributes<HTMLDivElement>>;
  onApplyCoupon?: (formData: any) => void;
}

export const Coupons: FunctionComponent<CouponsProps> = ({
  accordionSectionTitle,
  accordionSectionIcon,
  className,
  children,
  couponCodeField,
  applyCouponsButton,
  appliedCoupons,
  error,
  onApplyCoupon,
  ...props
}) => {
  const couponFormRef = useRef<HTMLFormElement>(null);

  const dictionary = useText({
    couponTitle: 'Cart.PriceSummary.coupon.title',
  });

  const onSubmitCoupons = (e: Event) => {
    e.preventDefault();

    const formData = getFormValues(couponFormRef.current!);

    onApplyCoupon?.(formData);

    const input = couponFormRef?.current?.querySelector('input');
    if (input) {
      input.value = '';
    }
  };

  return (
    <div
      {...props}
      data-testid="cart-coupons"
      className={classes(['cart-coupons', className])}
    >
      <Accordion
        data-testid="coupon-code"
        className={classes(['cart-coupons__accordion'])}
        actionIconPosition="right"
        iconOpen={Add}
        iconClose={Minus}
      >
        <AccordionSection
          title={accordionSectionTitle ?? dictionary.couponTitle}
          iconLeft={accordionSectionIcon ?? Coupon}
          showIconLeft={true}
          renderContentWhenClosed={false}
          data-testid="coupon-code-accordion-section"
          className={classes(['cart-coupons__accordion-section'])}
        >
          <form
            data-testid="coupon-code-form"
            className={classes(['coupon-code-form--edit'])}
            ref={couponFormRef}
          >
            <div className={classes(['coupon-code-form__action'])}>
              {couponCodeField && (
                <VComponent
                  node={couponCodeField}
                  className={classes(['coupon-code-form__codes'])}
                />
              )}
              {applyCouponsButton && (
                <VComponent
                  node={applyCouponsButton}
                  className={classes(['coupon-code-form--button'])}
                  onClick={onSubmitCoupons}
                  type="submit"
                />
              )}
            </div>
          </form>

          {error && (
            <VComponent
              node={error}
              className={classes(['coupon-code-form__error'])}
            />
          )}

          {appliedCoupons && (
            <VComponent
              node={appliedCoupons}
              className={classes(['coupon-code-form__applied'])}
            />
          )}
        </AccordionSection>
      </Accordion>
    </div>
  );
};
